/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { snippetApi } from '../../services/api';
import type { SnippetListItem, SnippetFilters } from '../../types';

const SnippetList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<SnippetFilters>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snippetToDelete, setSnippetToDelete] = useState<number | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [snippetToDuplicate, setSnippetToDuplicate] = useState<number | null>(null);
  const [newSnippetName, setNewSnippetName] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);

  // Fetch snippets
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['snippets', filters, page, rowsPerPage],
    queryFn: () => snippetApi.list(filters),
  });

  // Fetch categories for filter
  const { data: categories } = useQuery({
    queryKey: ['snippet-categories'],
    queryFn: snippetApi.getCategories,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: snippetApi.delete,
    onSuccess: () => {
      toast.success('Snippet deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
      setDeleteDialogOpen(false);
    },
    onError: () => {
      toast.error('Failed to delete snippet');
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      snippetApi.duplicate(id, name),
    onSuccess: () => {
      toast.success('Snippet duplicated successfully');
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
      setDuplicateDialogOpen(false);
      setNewSnippetName('');
    },
    onError: () => {
      toast.error('Failed to duplicate snippet');
    },
  });

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: event.target.value });
    setPage(0);
  };

  const handleCategoryChange = (event: any) => {
    setFilters({ ...filters, category: event.target.value });
    setPage(0);
  };

  const handleDelete = (id: number) => {
    setSnippetToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (snippetToDelete) {
      deleteMutation.mutate(snippetToDelete);
    }
  };

  const handleDuplicate = (id: number, name: string) => {
    setSnippetToDuplicate(id);
    setNewSnippetName(`${name}_copy`);
    setDuplicateDialogOpen(true);
  };

  const confirmDuplicate = () => {
    if (snippetToDuplicate && newSnippetName) {
      duplicateMutation.mutate({ id: snippetToDuplicate, name: newSnippetName });
    }
  };

  const snippets = data?.results || [];

  return (
    <Box>
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between" 
        alignItems={isMobile ? 'stretch' : 'center'} 
        gap={2}
        mb={3}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'}>Prompt Snippets</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/ai/snippets/new')}
          fullWidth={isMobile}
        >
          New Snippet
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {isMobile && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={filtersExpanded ? 2 : 0}>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              size="small"
            >
              {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <IconButton onClick={() => refetch()} color="primary" size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
        <Collapse in={!isMobile || filtersExpanded}>
          <Box 
            display="flex" 
            flexDirection={isMobile ? 'column' : 'row'}
            gap={2} 
            alignItems={isMobile ? 'stretch' : 'center'}
          >
            <TextField
              placeholder="Search snippets..."
              value={filters.search || ''}
              onChange={handleSearchChange}
              size="small"
              fullWidth={isMobile}
              sx={{ flex: isMobile ? undefined : 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                style: { fontSize: 16 }
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: isMobile ? undefined : 200 }} fullWidth={isMobile}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ''}
                onChange={handleCategoryChange}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories?.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isMobile && (
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Mobile Card View */}
      {isMobile ? (
        <Box>
          <Grid container spacing={2}>
            {isLoading ? (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography>Loading...</Typography>
                </Paper>
              </Grid>
            ) : snippets.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No snippets found. Create your first snippet!</Typography>
                </Paper>
              </Grid>
            ) : (
              snippets.map((snippet) => (
                <Grid size={{ xs: 12 }} key={snippet.id}>
                  <Card>
                    <CardActionArea onClick={() => navigate(`/ai/snippets/${snippet.id}`)}>
                      <CardContent sx={{ pb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {snippet.name}
                        </Typography>
                        {snippet.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {snippet.description}
                          </Typography>
                        )}
                        <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                          {snippet.category && (
                            <Chip label={snippet.category} size="small" />
                          )}
                          <Chip
                            label={`${snippet.usage_count} uses`}
                            size="small"
                            color={snippet.usage_count > 0 ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    <Box display="flex" justifyContent="flex-end" px={1} pb={1} gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(snippet.id, snippet.name)}
                      >
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(snippet.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
          <Paper sx={{ mt: 2 }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data?.count || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Per page:"
            />
          </Paper>
        </Box>
      ) : (
      /* Desktop Table View */
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="center">Usage</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : snippets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No snippets found. Create your first snippet!
                </TableCell>
              </TableRow>
            ) : (
              snippets.map((snippet) => (
                <TableRow
                  key={snippet.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/ai/snippets/${snippet.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {snippet.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {snippet.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {snippet.category && (
                      <Chip label={snippet.category} size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {snippet.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                      {snippet.tags.length > 3 && (
                        <Chip label={`+${snippet.tags.length - 3}`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={snippet.usage_count}
                      size="small"
                      color={snippet.usage_count > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatStoredDate(snippet.created_at, undefined, '-')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/ai/snippets/${snippet.id}`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDuplicate(snippet.id, snippet.name)}
                    >
                      <DuplicateIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(snippet.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data?.count || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this snippet? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Duplicate Snippet</DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New Snippet Name"
            fullWidth
            value={newSnippetName}
            onChange={(e) => setNewSnippetName(e.target.value)}
            helperText="Enter a unique name for the duplicated snippet"
            InputProps={{ style: { fontSize: 16 } }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDuplicate}
            variant="contained"
            disabled={!newSnippetName || duplicateMutation.isPending}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnippetList;
